import {
  MICROS_PER_USD,
  setTenantAiBudgetRequestSchema,
  type AiJobCounts,
  type ProvisioningRun,
} from '@avrahami/contracts';

/**
 * The content engine's numbers, as the platform admin shows them (docs/AI_CONTENT.md, "Cost
 * control"). Amounts arrive in micro-dollars (millionths of a US dollar): the provider's bill.
 */

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Micro-dollars as dollars with two decimals: 12_340_000 → "$12.34". */
export function formatUsdMicros(micros: number): string {
  return usd.format(micros / MICROS_PER_USD);
}

const counts = new Intl.NumberFormat('he-IL');

/** A count with thousands separators (tokens run into the millions). */
export function formatCount(value: number): string {
  return counts.format(value);
}

const months = new Intl.DateTimeFormat('he-IL', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** The month the amounts belong to. The period starts at midnight UTC, so it is read in UTC. */
export function formatPeriodMonth(periodStart: string): string {
  return months.format(new Date(periodStart));
}

/** A whole percentage of `part` out of `whole`, between 0 and 100. Nothing out of nothing is 0. */
export function percentOf(part: number, whole: number): number {
  if (whole <= 0) return part > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, Math.floor((part / whole) * 100)));
}

/** From this share of a limit on, the screen warns that it is about to be reached. */
export const NEAR_LIMIT_PERCENT = 80;

/**
 * - OK: well inside the limit
 * - NEAR: most of the limit is used
 * - REACHED: the limit is used up, and the engine pauses until next month or a higher limit (a
 *   limit of zero is reached from the start)
 */
export type BudgetState = 'OK' | 'NEAR' | 'REACHED';

export function budgetState(spentMicros: number, limitMicros: number): BudgetState {
  if (spentMicros >= limitMicros) return 'REACHED';
  return percentOf(spentMicros, limitMicros) >= NEAR_LIMIT_PERCENT ? 'NEAR' : 'OK';
}

/** Jobs the worker still has to do: while there are any, the numbers keep moving. */
export function hasOpenJobs(jobs: AiJobCounts): boolean {
  return jobs.queued + jobs.running > 0;
}

/** How often the content screens ask again while the engine is working. */
export const AI_POLL_MS = 5_000;

/** The refresh interval of a content query: light polling while jobs are open, none otherwise. */
export function aiPollInterval(jobs: readonly AiJobCounts[] | undefined): number | false {
  return jobs?.some(hasOpenJobs) === true ? AI_POLL_MS : false;
}

/** The run stands at the content step until every product has copy; then it opens the store. */
export function waitsForContent(run: ProvisioningRun | null | undefined): boolean {
  return run?.status === 'WAITING' && run.currentStep === 'GENERATE_CONTENT';
}

/**
 * How often the provisioning card asks again: often while the worker is on the run, lightly
 * while the copy is written (the run then continues by itself), never once it stopped.
 */
export function provisioningPollInterval(run: ProvisioningRun | null | undefined): number | false {
  if (run === null || run === undefined) return false;
  if (run.status === 'PENDING' || run.status === 'RUNNING') return 3_000;
  return waitsForContent(run) && run.waitingReason === 'CONTENT_IN_PROGRESS' ? AI_POLL_MS : false;
}

const USD_INPUT = /^(\d{1,6})(?:\.(\d{1,2}))?$/;

/**
 * A monthly limit typed in dollars ("250", "12.5", "0.99") as micro-dollars. Whole dollars and
 * at most two decimals, within the contract's range (0 to 100,000 dollars); anything else is
 * `null`. The amount is built from the digits, never from a float.
 */
export function parseUsdLimit(text: string): number | null {
  const match = USD_INPUT.exec(text.trim());
  if (match === null) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  const micros = cents * (MICROS_PER_USD / 100);
  return setTenantAiBudgetRequestSchema.safeParse({ monthlyLimitMicros: micros }).success
    ? micros
    : null;
}

/** A limit as the text of the dollars field: 12_500_000 → "12.50". */
export function usdLimitInput(micros: number): string {
  return (micros / MICROS_PER_USD).toFixed(2);
}
